import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Notification from 'components/notification';
import { SessionContext } from 'contexts/session';
import { ROLES } from 'constants/roles';
import { hasCustomContent as hasCustomContentService } from 'services/customContent';
import { FEATURE_FLAGS, useBooleanFlag } from './useFeatureFlags';

export interface IUseCustomContentAvailabilityResult {
  hasCustomContent: boolean;
  isCheckingCustomContent: boolean;
  refreshCustomContentAvailability: () => Promise<boolean>;
}

interface IUseCustomContentAvailabilityOptions {
  skipInitialCheck?: boolean;
}

const useCustomContentAvailability = (
  options: IUseCustomContentAvailabilityOptions = {}
): IUseCustomContentAvailabilityResult => {
  const { skipInitialCheck = false } = options;
  const session = useContext(SessionContext);
  const customContentEnabled = useBooleanFlag(FEATURE_FLAGS.CUSTOM_CONTENT_ENABLED);
  const isCustomContentEnabled = Boolean(customContentEnabled);
  const userRoleIds = useMemo(
    () => session?.userRoleIds || session?.user?.uaa_data?.roles?.data?.map(({ id }) => id) || [],
    [session?.userRoleIds, session?.user?.uaa_data?.roles?.data]
  );
  const isAdminUser = useMemo(() => userRoleIds.includes(ROLES.ADMIN), [userRoleIds]);
  const isAccountOwner = useMemo(() => userRoleIds.includes(ROLES.ACCOUNT_OWNER), [userRoleIds]);
  const isTeamManager = useMemo(() => userRoleIds.includes(ROLES.TEAM_MANAGER), [userRoleIds]);
  const canAccessCustomContent = isAccountOwner || isTeamManager;
  const [hasCustomContent, setHasCustomContent] = useState(false);
  const hasCustomContentRef = useRef(hasCustomContent);
  const [isCheckingCustomContent, setIsCheckingCustomContent] = useState(
    isCustomContentEnabled && !skipInitialCheck && !isAdminUser && canAccessCustomContent
  );

  useEffect(() => {
    hasCustomContentRef.current = hasCustomContent;
  }, [hasCustomContent]);

  const refreshCustomContentAvailability = useCallback(async () => {
    if (!isCustomContentEnabled || isAdminUser || !canAccessCustomContent) {
      setHasCustomContent(false);
      setIsCheckingCustomContent(false);
      return false;
    }

    setIsCheckingCustomContent(true);

    try {
      const exists = await hasCustomContentService(isCustomContentEnabled);
      setHasCustomContent(exists);
      return exists;
    } catch (error) {
      Notification({ text: 'There has been a problem fetching custom content', type: 'error' });
      return hasCustomContentRef.current;
    } finally {
      setIsCheckingCustomContent(false);
    }
  }, [canAccessCustomContent, isAdminUser, isCustomContentEnabled]);

  useEffect(() => {
    if (skipInitialCheck) return;
    refreshCustomContentAvailability();
  }, [refreshCustomContentAvailability, skipInitialCheck]);

  return {
    hasCustomContent,
    isCheckingCustomContent,
    refreshCustomContentAvailability,
  };
};

export default useCustomContentAvailability;
